import React from "react";
import { Box, Paper, Typography, Grid, Tooltip, Stack, useTheme, Chip } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

interface Segment {
  type: "text" | "upgrade";
  text: string;
  originalText?: string;
  explanation?: string;
}

interface SentenceItem {
  originalSentence: string;
  upgradedSentence: string;
  isModified?: boolean;
  segments?: Segment[];
}

interface ParagraphItem {
  sectionType: string;
  originalParagraph: string;
  upgradedParagraph: string;
  sentences: SentenceItem[];
}

export interface UpgradedResultData {
  upgradedEssay?: string;
  overallExplanation?: string;
  paragraphs?: ParagraphItem[];
  segments?: Segment[];
}

interface Props {
  prompt?: string;
  originalEssay: string;
  upgradedResult: UpgradedResultData;
  currentBand?: number;
  targetBand?: number;
}

interface SectionColorTheme {
  bgLight: string;
  borderLight: string;
  textLight: string;
  bgDark: string;
  borderDark: string;
  textDark: string;
  legendLabel: string;
}

const SECTION_COLOR_SCHEMES: Record<number, SectionColorTheme> = {
  0: {
    bgLight: "#FFF1F2",
    borderLight: "#FECDD3",
    textLight: "#BE185D",
    bgDark: "rgba(244, 114, 182, 0.12)",
    borderDark: "rgba(244, 114, 182, 0.25)",
    textDark: "#F472B6",
    legendLabel: "Màu 1 - Introduction",
  },
  1: {
    bgLight: "#F0F9FF",
    borderLight: "#BAE6FD",
    textLight: "#0369A1",
    bgDark: "rgba(56, 189, 248, 0.12)",
    borderDark: "rgba(56, 189, 248, 0.25)",
    textDark: "#38BDF8",
    legendLabel: "Màu 2 - Body Paragraph 1",
  },
  2: {
    bgLight: "#F0FDF4",
    borderLight: "#BBF7D0",
    textLight: "#15803D",
    bgDark: "rgba(74, 222, 128, 0.12)",
    borderDark: "rgba(74, 222, 128, 0.25)",
    textDark: "#4ADE80",
    legendLabel: "Màu 3 - Body Paragraph 2",
  },
  3: {
    bgLight: "#FEFCE8",
    borderLight: "#FEF08A",
    textLight: "#C2410C",
    bgDark: "rgba(251, 146, 60, 0.12)",
    borderDark: "rgba(251, 146, 60, 0.25)",
    textDark: "#FB923C",
    legendLabel: "Màu 4 - Body Paragraph 3",
  },
  4: {
    bgLight: "#FAF5FF",
    borderLight: "#E9D5FF",
    textLight: "#7E22CE",
    bgDark: "rgba(192, 132, 252, 0.12)",
    borderDark: "rgba(192, 132, 252, 0.25)",
    textDark: "#C084FC",
    legendLabel: "Màu 5 - Conclusion",
  },
};

const getSectionName = (index: number, total: number): string => {
  if (index === 0) return "Introduction";
  if (index === total - 1 && total > 2) return "Conclusion";
  return `Body Paragraph ${index}`;
};

const splitIntoSentences = (text: string): string[] => {
  if (!text) return [];
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
};

export const IeltsWritingUpgradeTable: React.FC<Props> = ({
  prompt,
  originalEssay,
  upgradedResult,
  currentBand,
  targetBand,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Normalize data into paragraphs & sentences array
  const parsedParagraphs: ParagraphItem[] = React.useMemo(() => {
    const splitSentences = (text: string): string[] => {
      if (!text) return [];
      return text
        .split(/(?<=[.!?])(?:\s+|\n+)/)
        .map((s) => s.trim())
        .filter(Boolean);
    };

    if (upgradedResult.paragraphs && upgradedResult.paragraphs.length > 0) {
      return upgradedResult.paragraphs.map((p, pIdx) => {
        const origSentences = splitSentences(p.originalParagraph || "");
        const upgSentences = splitSentences(p.upgradedParagraph || "");

        // If backend provided pre-split sentences with more than 1 item and matching counts
        if (p.sentences && p.sentences.length > 1 && p.sentences.length >= origSentences.length) {
          return {
            sectionType: p.sectionType || getSectionName(pIdx, upgradedResult.paragraphs!.length),
            originalParagraph: p.originalParagraph || "",
            upgradedParagraph: p.upgradedParagraph || "",
            sentences: p.sentences,
          };
        }

        // Otherwise split sentence-by-sentence by punctuation!
        const maxCount = Math.max(origSentences.length, upgSentences.length, 1);
        const sentences: SentenceItem[] = [];

        for (let j = 0; j < maxCount; j++) {
          const oSent = origSentences[j] || "";
          const uSent = upgSentences[j] || oSent;
          const isModified = oSent.trim() !== uSent.trim();

          let sentenceSegments: Segment[] | undefined = undefined;
          if (p.sentences && p.sentences[0] && p.sentences[0].segments) {
            sentenceSegments = p.sentences[0].segments.filter(
              (seg) => seg.type === "upgrade" && uSent.includes(seg.text)
            );
          } else if (upgradedResult.segments && isModified) {
            sentenceSegments = upgradedResult.segments.filter(
              (seg) => seg.type === "upgrade" && uSent.includes(seg.text)
            );
          }

          sentences.push({
            originalSentence: oSent,
            upgradedSentence: uSent,
            isModified,
            segments: sentenceSegments,
          });
        }

        return {
          sectionType: p.sectionType || getSectionName(pIdx, upgradedResult.paragraphs!.length),
          originalParagraph: p.originalParagraph || "",
          upgradedParagraph: p.upgradedParagraph || "",
          sentences,
        };
      });
    }

    // Fallback parsing if backend didn't return pre-chunked paragraphs
    const origParas = originalEssay
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);

    const fullUpgradedText = upgradedResult.upgradedEssay || upgradedResult.segments?.map((s) => s.text).join("") || "";

    const upgParas = fullUpgradedText
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);

    const count = Math.max(origParas.length, upgParas.length, 1);
    const result: ParagraphItem[] = [];

    for (let i = 0; i < count; i++) {
      const origP = origParas[i] || "";
      const upgP = upgParas[i] || origP;

      const origSentences = splitSentences(origP);
      const upgSentences = splitSentences(upgP);

      const maxSentences = Math.max(origSentences.length, upgSentences.length, 1);
      const sentences: SentenceItem[] = [];

      for (let j = 0; j < maxSentences; j++) {
        const oSent = origSentences[j] || "";
        const uSent = upgSentences[j] || oSent;
        const isModified = oSent.trim() !== uSent.trim();

        let sentenceSegments: Segment[] | undefined = undefined;
        if (upgradedResult.segments && isModified) {
          sentenceSegments = upgradedResult.segments.filter(
            (seg) => seg.type === "upgrade" && uSent.includes(seg.text)
          );
        }

        sentences.push({
          originalSentence: oSent,
          upgradedSentence: uSent,
          isModified,
          segments: sentenceSegments,
        });
      }

      result.push({
        sectionType: getSectionName(i, count),
        originalParagraph: origP,
        upgradedParagraph: upgP,
        sentences,
      });
    }

    return result;
  }, [originalEssay, upgradedResult]);

  const renderFormattedExplanation = (rawText: string) => {
    if (!rawText) return null;

    const formattedText = rawText
      .replace(/(?:;\s*|\s+)(?=\(\d+\))/g, "\n- ")
      .replace(/(?::\s*|;\s*)(?=\d+[\.\)]\s)/g, ":\n- ");

    const lines = formattedText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    return (
      <Stack spacing={1}>
        {lines.map((line, lineIdx) => {
          const isBullet = line.startsWith("-") || line.startsWith("*");
          const content = isBullet ? line.substring(1).trim() : line;

          const parts = content.split(/(\*\*.*?\*\*)/g);
          const elements = parts.map((part, partIdx) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              return <strong key={partIdx}>{part.slice(2, -2)}</strong>;
            }
            return part;
          });

          if (isBullet) {
            return (
              <Box
                key={lineIdx}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.2,
                  fontSize: "0.85rem",
                  color: isDark ? "#CBD5E1" : "#334155",
                  lineHeight: 1.6,
                  pl: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "#8B5CF6",
                    mt: 0.9,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1 }}>{elements}</Box>
              </Box>
            );
          }

          return (
            <Typography
              key={lineIdx}
              variant="body2"
              sx={{
                fontSize: "0.875rem",
                color: isDark ? "#E2E8F0" : "#1E293B",
                lineHeight: 1.6,
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              {elements}
            </Typography>
          );
        })}
      </Stack>
    );
  };

  const renderUpgradedSentenceContent = (sentence: SentenceItem, themeColor: string) => {
    const upgradedSentence = sentence.upgradedSentence || "";

    // If not modified ("câu nào k sửa thì thôi")
    if (!sentence.isModified) {
      return (
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontSize: "0.875rem",
            lineHeight: 1.7,
            color: isDark ? "#E2E8F0" : "#334155",
          }}
        >
          {upgradedSentence}
        </Typography>
      );
    }

    // Extract upgrade segments present in this sentence
    const upgradeSegments = (sentence.segments || []).filter(
      (seg) => seg.type === "upgrade" && seg.text && upgradedSentence.includes(seg.text)
    );

    if (upgradeSegments.length === 0) {
      return (
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontSize: "0.875rem",
            lineHeight: 1.7,
            color: isDark ? "#F472B6" : "#A21CAF",
            fontWeight: 600,
          }}
        >
          {upgradedSentence}
        </Typography>
      );
    }

    // Split upgradedSentence by upgrade segment texts to preserve non-upgraded text
    const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = new RegExp(`(${upgradeSegments.map((u) => escapeRegex(u.text)).join("|")})`, "g");

    const parts = upgradedSentence.split(pattern);

    return (
      <Box component="span" sx={{ fontSize: "0.875rem", lineHeight: 1.7, color: isDark ? "#E2E8F0" : "#334155" }}>
        {parts.map((part, pIdx) => {
          const matchingSeg = upgradeSegments.find((u) => u.text === part);
          if (matchingSeg) {
            return (
              <Tooltip
                key={pIdx}
                title={
                  <Box sx={{ p: 0.5, fontSize: "0.75rem", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                    {matchingSeg.originalText && (
                      <Box sx={{ fontWeight: 700, color: "#EC4899", mb: 0.5 }}>Gốc: "{matchingSeg.originalText}"</Box>
                    )}
                    {matchingSeg.explanation || "Được tối ưu hoá từ vựng/ngữ pháp"}
                  </Box>
                }
                arrow
                placement="top"
                enterDelay={100}
              >
                <span
                  style={{
                    color: isDark ? "#F472B6" : "#A21CAF",
                    fontWeight: 700,
                    cursor: "help",
                    backgroundColor: isDark ? "rgba(244, 114, 182, 0.2)" : "rgba(240, 215, 255, 0.65)",
                    padding: "2px 6px",
                    margin: "0 2px",
                    borderRadius: "4px",
                  }}
                >
                  {matchingSeg.text}
                </span>
              </Tooltip>
            );
          }
          return <span key={pIdx}>{part}</span>;
        })}
      </Box>
    );
  };

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      {/* Top Section: Title, Prompt, and Color-Code Legend */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
          bgcolor: isDark ? "rgba(30, 41, 59, 0.6)" : "#F8FAFC",
        }}
      >
        <Grid container spacing={2.5} alignItems="flex-start">
          {/* Left: Title & Question Prompt */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <AutoAwesomeIcon sx={{ color: "#8B5CF6", fontSize: 22 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, fontSize: "1.05rem", color: isDark ? "#F1F5F9" : "#0F172A" }}
              >
                IELTS Writing – Corrections (Color Coded)
              </Typography>
              {currentBand !== undefined && targetBand !== undefined && (
                <Chip
                  label={`Band ${currentBand} ➔ Band ${targetBand}`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: "rgba(139, 92, 246, 0.15)",
                    color: "#8B5CF6",
                    borderRadius: "8px",
                  }}
                />
              )}
            </Box>

            {prompt && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  bgcolor: isDark ? "rgba(0,0,0,0.2)" : "#FFFFFF",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.5 }}
                >
                  ĐỀ BÀI (PROMPT):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.825rem", color: isDark ? "#CBD5E1" : "#334155", fontStyle: "italic" }}
                >
                  "{prompt}"
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Right: Color Legend Card */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: "14px",
                bgcolor: isDark ? "rgba(15, 23, 42, 0.7)" : "#FFFFFF",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", display: "block", mb: 1 }}>
                CHÚ THÍCH MÀU THEO ĐOẠN (COLOR CODES):
              </Typography>
              <Stack spacing={0.75}>
                {Object.entries(SECTION_COLOR_SCHEMES).map(([idxStr, scheme]) => {
                  const schemeIndex = Number(idxStr);
                  return (
                    <Box key={schemeIndex} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "4px",
                          bgcolor: isDark ? scheme.textDark : scheme.borderLight,
                          border: `1px solid ${isDark ? scheme.borderDark : scheme.textLight}`,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          color: isDark ? scheme.textDark : scheme.textLight,
                        }}
                      >
                        {scheme.legendLabel}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Side-by-Side Comparison Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        }}
      >
        {/* Table Header Bar */}
        <Grid container sx={{ bgcolor: isDark ? "#0F172A" : "#1E293B", color: "#FFFFFF", px: 2, py: 1.5 }}>
          <Grid item xs={6} sx={{ pr: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, textTransform: "uppercase", tracking: 1, letterSpacing: "0.5px" }}
            >
              ORIGINAL (CÂU GỐC)
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ pl: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, textTransform: "uppercase", tracking: 1, letterSpacing: "0.5px" }}
            >
              CORRECTED (CÂU ĐÃ SỬA AI)
            </Typography>
          </Grid>
        </Grid>

        {/* Table Rows per Paragraph / Section */}
        {parsedParagraphs.map((para, pIdx) => {
          const scheme = SECTION_COLOR_SCHEMES[pIdx % 5];
          const bg = isDark ? scheme.bgDark : scheme.bgLight;
          const borderClr = isDark ? scheme.borderDark : scheme.borderLight;
          const textClr = isDark ? scheme.textDark : scheme.textLight;

          return (
            <Box
              key={pIdx}
              sx={{
                bgcolor: bg,
                borderBottom: pIdx < parsedParagraphs.length - 1 ? `1px solid ${borderClr}` : "none",
                p: 2,
              }}
            >
              {/* Section Tag */}
              <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={para.sectionType || scheme.legendLabel}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    bgcolor: isDark ? scheme.borderDark : scheme.borderLight,
                    color: textClr,
                    borderRadius: "6px",
                  }}
                />
              </Box>

              {/* Sentences Alignment inside Paragraph */}
              <Stack spacing={1.5}>
                {para.sentences.map((sent, sIdx) => {
                  const isMod = sent.isModified;
                  return (
                    <Grid
                      container
                      key={sIdx}
                      spacing={2}
                      alignItems="flex-start"
                      sx={{
                        pt: sIdx > 0 ? 1.5 : 0,
                        pb: sIdx < para.sentences.length - 1 ? 1.5 : 0,
                        borderTop:
                          sIdx > 0 ? `1px dashed ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}` : "none",
                      }}
                    >
                      {/* Left Column: Original Sentence */}
                      <Grid item xs={6}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.875rem",
                            lineHeight: 1.7,
                            color: isMod ? (isDark ? "#F87171" : "#DC2626") : textClr,
                            fontWeight: isMod ? 600 : 400,
                          }}
                        >
                          {sent.originalSentence || "(Trống)"}
                        </Typography>
                      </Grid>

                      {/* Right Column: Upgraded Sentence */}
                      <Grid item xs={6}>
                        {renderUpgradedSentenceContent(sent, textClr)}
                      </Grid>
                    </Grid>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Paper>

      {/* Overall AI Explanation Card */}
      {upgradedResult.overallExplanation && (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: "16px",
            bgcolor: isDark ? "rgba(139, 92, 246, 0.05)" : "rgba(139, 92, 246, 0.02)",
            borderColor: isDark ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.15)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <AutoAwesomeIcon sx={{ color: "#8B5CF6", fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#8B5CF6" }}>
              Tại sao bài gợi ý này nâng được band điểm?
            </Typography>
          </Box>
          <Box sx={{ mt: 1 }}>{renderFormattedExplanation(upgradedResult.overallExplanation)}</Box>
        </Paper>
      )}
    </Stack>
  );
};

export default IeltsWritingUpgradeTable;
